import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { financeService } from '../services/api';
import MessagesSidebar from '../components/MessagesSidebar';

/* ─── küçük yardımcılar ─── */
const TextInput = ({ placeholder, value, onChange, type = 'text' }) => (
    <input
        type={type}
        value={value ?? ''}
        onChange={onChange}
        placeholder={placeholder}
        dir="rtl"
        className="w-full bg-white border border-[#D6E1F5] rounded-xl px-4 py-2.5 text-xs sm:text-sm text-[#204286] placeholder:text-gray-300 focus:outline-none focus:ring-2 focus:ring-[#292DD8]/30 transition-all"
    />
);

const FieldLabel = ({ children }) => (
    <p className="text-xs sm:text-sm font-bold text-[#204286] text-right mb-1.5">{children}</p>
);

const Toast = ({ message, type, onClose }) => (
    <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 40 }}
        className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-6 py-3 rounded-xl text-white text-sm font-bold shadow-xl ${
            type === 'success' ? 'bg-green-500' : 'bg-red-500'
        }`}
    >
        <div className="flex items-center gap-2">
            <span>{message}</span>
            <button onClick={onClose} className="opacity-70 hover:opacity-100">✕</button>
        </div>
    </motion.div>
);

const emptyCheck = () => ({
    id: `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    number: '', issued_at: '', amount: '', bank: '', image: null, existingImage: null
});

/* ─── main ─── */
const FinanceChecks = () => {
    const navigate = useNavigate();
    const { code } = useParams();
    const { language } = useLanguage();
    const isRtl = language === 'fa';
    const t = (fa, en) => (isRtl ? fa : en);

    const [checks, setChecks] = useState([emptyCheck()]);
    const [pageLoading, setPageLoading] = useState(true);
    const [pageError, setPageError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [toast, setToast] = useState(null);

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 3500);
    };

    /* ── mevcut çekleri getir ── */
    useEffect(() => {
        const init = async () => {
            setPageLoading(true);
            setPageError(null);
            try {
                const data = await financeService.getChecks(code);
                if (Array.isArray(data) && data.length > 0) {
                    setChecks(
                        data.map((c, idx) => ({
                            id: `existing-${idx}`,
                            number: c.number || '',
                            issued_at: c.issued_at || '',
                            amount: c.amount ?? '',
                            bank: c.bank || '',
                            image: null,
                            existingImage: c.image || null
                        }))
                    );
                } else {
                    setChecks([emptyCheck()]);
                }
            } catch (err) {
                console.error('Checks fetch error:', err);
                setPageError(t('خطا در دریافت چک‌ها', 'Error loading checks'));
            } finally {
                setPageLoading(false);
            }
        };
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [code]);

    const addCheck = () => setChecks((prev) => [...prev, emptyCheck()]);

    const removeCheck = (id) =>
        setChecks((prev) => (prev.length > 1 ? prev.filter((c) => c.id !== id) : prev));

    const updateCheck = (id, field, value) =>
        setChecks((prev) => prev.map((c) => (c.id === id ? { ...c, [field]: value } : c)));

    const handleCheckFile = (id, e) => {
        const file = e.target.files?.[0];
        if (file) updateCheck(id, 'image', file);
    };

    /* ── submit: tüm liste POST ile replace edilir ── */
    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const payload = checks
                .filter((c) => c.number.trim() || c.issued_at.trim() || c.amount !== '' || c.bank.trim())
                .map((c) => ({
                    number: c.number.trim(),
                    issued_at: c.issued_at.trim(),
                    amount: c.amount === '' ? null : Number(c.amount),
                    bank: c.bank.trim(),
                    // sadece kullanıcının yeni seçtiği File nesnesi gönderilir;
                    // mevcut görselin URL'i (existingImage) form-data'ya dosya olarak eklenmez
                    image: c.image || null
                }));

            await financeService.replaceChecks(code, payload);
            showToast(t('چک‌ها با موفقیت ذخیره شد', 'Checks saved successfully'));
        } catch (err) {
            console.error('Checks submit error:', err);
            showToast(
                t('خطا در ذخیره چک‌ها. دوباره تلاش کنید.', 'Error saving checks. Please try again.'),
                'error'
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="max-w-[1136px] mx-auto px-3 sm:px-4 py-4 lg:py-8 font-iransansRegular overflow-hidden"
            dir={isRtl ? 'rtl' : 'ltr'}
        >
            <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
                <MessagesSidebar
                    activeKey="finance"
                    onNavigate={(key) => {
                        if (key === 'messages') navigate('/messages');
                        if (key === 'finance') navigate('/finance');
                        if (key === 'account') navigate('/account');
                        if (key === 'support') navigate('/support');
                        if (key === 'logout') navigate('/logout');
                    }}
                />

                <div className="flex-1 w-full min-w-0">
                    {/* Title + back */}
                    <div className="flex items-center justify-between mb-5 lg:mb-7">
                        <motion.h1
                            className="text-xl sm:text-2xl lg:text-[32px] font-extrabold text-[#292DD8] text-right"
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                        >
                            {t('بارگزاری چک‌های ضمانت', 'Upload Guarantee Checks')}
                        </motion.h1>
                        <button
                            type="button"
                            onClick={() => navigate('/finance')}
                            className="text-xs sm:text-sm font-bold text-[#204286] hover:underline whitespace-nowrap"
                        >
                            {t('بازگشت به لیست', 'Back to List')}
                        </button>
                    </div>

                    {/* code chip */}
                    <p className="text-xs sm:text-sm text-gray-400 mb-5 text-right">
                        {t('کد پیگیری پرداخت', 'Payment Tracking Code')}: <span className="font-bold text-[#204286]">{code}</span>
                    </p>

                    {pageLoading && (
                        <div className="flex justify-center items-center min-h-[40vh]">
                            <div className="flex flex-col items-center gap-4">
                                <div className="w-10 h-10 border-4 border-[#292DD8] border-t-transparent rounded-full animate-spin" />
                                <p className="text-[#292DD8] font-bold text-sm">
                                    {t('در حال بارگذاری...', 'Loading...')}
                                </p>
                            </div>
                        </div>
                    )}

                    {!pageLoading && pageError && (
                        <div className="flex justify-center items-center min-h-[40vh]">
                            <div className="flex flex-col items-center gap-4 bg-red-50 rounded-xl p-8">
                                <p className="text-red-600 font-bold text-sm">{pageError}</p>
                                <button
                                    onClick={() => window.location.reload()}
                                    className="px-5 py-2 bg-red-500 text-white rounded-lg text-sm font-bold hover:bg-red-600 transition-colors"
                                >
                                    {t('تلاش مجدد', 'Try Again')}
                                </button>
                            </div>
                        </div>
                    )}

                    {!pageLoading && !pageError && (
                        <motion.form
                            onSubmit={handleSubmit}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                        >
                            <div className="bg-[#F5F7FB] rounded-2xl overflow-hidden mb-5">
                                <div className="px-4 sm:px-5 pt-4 sm:pt-5 pb-1 flex justify-end">
                                    <motion.button
                                        type="button"
                                        onClick={addCheck}
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.97 }}
                                        className="px-5 py-2 rounded-xl text-white font-bold text-xs sm:text-sm bg-gradient-to-r from-[#292DD8] to-[#4B4FEE] hover:from-[#1f23a8] hover:to-[#3a3ecc] shadow-md transition-all duration-300 flex items-center gap-1.5"
                                    >
                                        <span className="text-base leading-none">+</span>
                                        {t('افزودن چک', 'Add Check')}
                                    </motion.button>
                                </div>

                                <div className="px-4 sm:px-5 pb-5 sm:pb-6">
                                    <AnimatePresence>
                                        {checks.map((check, idx) => (
                                            <motion.div
                                                key={check.id}
                                                initial={{ opacity: 0, y: -10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                                                transition={{ duration: 0.25 }}
                                            >
                                                {idx > 0 && <div className="border-t border-[#D6E1F5] my-4" />}

                                                <div className="flex items-center justify-between mb-2">
                                                    <span className="text-[11px] sm:text-xs font-bold text-gray-400">
                                                        {t('چک', 'Check')} #{idx + 1}
                                                    </span>
                                                    {checks.length > 1 && (
                                                        <button
                                                            type="button"
                                                            onClick={() => removeCheck(check.id)}
                                                            className="text-[11px] sm:text-xs font-bold text-red-500 hover:underline"
                                                        >
                                                            {t('حذف', 'Remove')}
                                                        </button>
                                                    )}
                                                </div>

                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-3">
                                                    <div>
                                                        <FieldLabel>{t('شماره چک', 'Check Number')}</FieldLabel>
                                                        <TextInput
                                                            placeholder="مثال: ۱۲۳۴۵۶۷۸۹۰"
                                                            value={check.number}
                                                            onChange={(e) => updateCheck(check.id, 'number', e.target.value)}
                                                        />
                                                    </div>
                                                    <div>
                                                        <FieldLabel>{t('نام بانک', 'Bank')}</FieldLabel>
                                                        <TextInput
                                                            placeholder={t('مثال: بانک ملی', 'Example: Melli Bank')}
                                                            value={check.bank}
                                                            onChange={(e) => updateCheck(check.id, 'bank', e.target.value)}
                                                        />
                                                    </div>
                                                    <div>
                                                        <FieldLabel>{t('مبلغ', 'Amount')}</FieldLabel>
                                                        <TextInput
                                                            placeholder="مثال: ۱۰۰۰۰۰۰"
                                                            value={check.amount}
                                                            onChange={(e) => updateCheck(check.id, 'amount', e.target.value)}
                                                        />
                                                    </div>
                                                    <div>
                                                        <FieldLabel>{t('تاریخ چک', 'Check Date')}</FieldLabel>
                                                        <TextInput
                                                            type="date"
                                                            value={check.issued_at}
                                                            onChange={(e) => updateCheck(check.id, 'issued_at', e.target.value)}
                                                        />
                                                    </div>
                                                </div>

                                                {/* بارگزاری تصویر چک */}
                                                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mt-3">
                                                    <label htmlFor={`check-file-${check.id}`} className="cursor-pointer">
                                                        <div className="px-4 py-2 rounded-xl text-[#204286] font-bold text-xs sm:text-sm border border-[#204286] hover:bg-[#EBF1FB] transition-colors whitespace-nowrap">
                                                            {t('بارگزاری تصویر چک', 'Upload Check Image')}
                                                        </div>
                                                    </label>
                                                    <input
                                                        id={`check-file-${check.id}`}
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={(e) => handleCheckFile(check.id, e)}
                                                        className="hidden"
                                                    />
                                                    {check.image && (
                                                        <span className="text-xs text-[#204286] font-medium truncate max-w-[180px]">
                                                            {check.image.name}
                                                        </span>
                                                    )}
                                                    {!check.image && check.existingImage && (
                                                        <a
                                                            href={check.existingImage}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-xs text-[#292DD8] font-medium hover:underline"
                                                        >
                                                            {t('مشاهده تصویر فعلی', 'View current image')}
                                                        </a>
                                                    )}
                                                </div>
                                            </motion.div>
                                        ))}
                                    </AnimatePresence>
                                </div>
                            </div>

                            <div className="flex justify-center">
                                <motion.button
                                    type="submit"
                                    disabled={submitting}
                                    whileHover={{ scale: submitting ? 1 : 1.02 }}
                                    whileTap={{ scale: submitting ? 1 : 0.98 }}
                                    className="px-10 py-2.5 rounded-lg text-white font-bold text-sm sm:text-base shadow-lg bg-gradient-to-r from-[#292DD8] to-[#4B4FEE] hover:from-[#1f23a8] hover:to-[#3a3ecc] transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    {submitting
                                        ? t('در حال ذخیره...', 'Saving...')
                                        : t('ذخیره چک‌ها', 'Save Checks')}
                                </motion.button>
                            </div>
                        </motion.form>
                    )}
                </div>
            </div>

            <AnimatePresence>
                {toast && (
                    <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
                )}
            </AnimatePresence>
        </div>
    );
};

export default FinanceChecks;